'use client';

import { useState } from 'react';
import Turnstile from './Turnstile';
import RequiredAsterisk from './RequiredAsterisk';
import { useSubmitContact } from '@/lib/queries';

interface ContactFormProps {
  onSubmitSuccess?: () => void;
}

export default function ContactForm({ onSubmitSuccess }: ContactFormProps) {
  const [formData, setFormData] = useState({
    email: '',
    title: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileKey, setTurnstileKey] = useState<number>(0);

  const submitContact = useSubmitContact();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('idle');
    setErrorMessage('');

    if (!turnstileToken) {
      setErrorMessage('Please complete the security verification');
      return;
    }

    try {
      await submitContact.mutateAsync({
        ...formData,
        turnstileToken,
      });
      setSubmitStatus('success');
      setFormData({
        email: '',
        title: '',
        message: '',
      });
      setTurnstileToken('');
      setTurnstileKey((prev) => prev + 1); // Force Turnstile re-render
      onSubmitSuccess?.();
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to send message. Please try again.');
      }
    }
  };

  return (
    <div className="form-card">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-semibold text-gray-900 mb-2 dark:text-gray-100"
          >
            Email address <RequiredAsterisk />
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-green-500 focus:border-transparent"
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <label
            htmlFor="title"
            className="block text-sm font-semibold text-gray-900 mb-2 dark:text-gray-100"
          >
            Subject <RequiredAsterisk />
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-green-500 focus:border-transparent"
            placeholder="What is your message about?"
          />
        </div>

        <div>
          <label
            htmlFor="message"
            className="block text-sm font-semibold text-gray-900 mb-2 dark:text-gray-100"
          >
            Message <RequiredAsterisk />
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
            rows={6}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-green-500 focus:border-transparent resize-none"
            placeholder="Please provide as much detail as possible..."
          />
        </div>

        {/* Cloudflare Turnstile */}
        <div>
          <Turnstile
            key={turnstileKey}
            siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
            onVerify={setTurnstileToken}
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitContact.isPending || !turnstileToken}
          className="w-full bg-forest-green-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-forest-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {submitContact.isPending ? 'Sending...' : 'Send message'}
        </button>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-green-600 mr-3 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-green-800">Message sent successfully!</h3>
                <p className="mt-1 text-sm text-green-700">
                  Thank you for contacting us. We&apos;ll get back to you as soon as possible.
                </p>
              </div>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-600 mr-3 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Error sending message</h3>
                <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
